import API from "../service/axios_config";

export class AbstractAPI {
  static prefix = "/abstract";

  // ─── Abstract CRUD ────────────────────────────────────────────────────────

  static async insert(dataset) {
    const res = await API.post(`${this.prefix}/insert`, dataset);
    return res.data;
  }

  static async update(data) {
    if (data?.content_id === undefined || data?.content_id === null)
      throw new Error("content_id is required for update");

    const res = await API.put(`${this.prefix}/update`, data);
    return res.data;
  }

  static async getAll() {
    const res = await API.get(`${this.prefix}/all/abstract`);

    if (res.data?.success && Array.isArray(res.data.data)) {
      return res.data.data;
    }

    return Array.isArray(res.data) ? res.data : [];
  }

  // main content (active items, normalised)
  static async getActiveNormalized() {
    const res = await API.get(`${this.prefix}/all/abstract`);
    const apiItems = res.data?.data || [];

    const activeItems = apiItems.filter(
      (item) => String(item.status).toLowerCase() === "active",
    );

    const normalizedData = activeItems.map((item) => {
      const authorName =
        `${item.submitter_first_name || ""} ${item.submitter_last_name || ""}`.trim();

      const CoauthorName =
        [
          item?.person2_first_name,
          item?.person3_first_name,
          item?.person4_first_name,
          item?.person5_first_name,
        ]
          .filter((name) => typeof name === "string" && name.trim())
          .join(", ") || "-";

      return {
        id: item.content_id,
        title: item.title,
        year: String(item.years),
        category: item.category,
        author: authorName || "Unknown",
        Coauthor: CoauthorName || "Unknown",
        summaryabstract: item.summarised_abstract,
        summary: item.abstract,
        description: item.abstract,
        premium: false,
        subcategory: item.sub_category,
        subdata: [
          {
            id: `${item.content_id}-1`,
            title: item.title,
            summary: item.abstract,
            summaryabstract: item.summarised_abstract,
            author: authorName || "Unknown",
            Coauthor: CoauthorName || "Unknown",
            subcategory: item.sub_category,
            category: item.category,
            year: String(item.years),
            link: "",
            filepath: item.file_path,
          },
        ],
      };
    });

    return normalizedData;
  }

  // categories and years (used by admin/youtube)
  static async getUniqueCategoriesAndYears() {
    const list = await this.getAll();

    const uniqueCategories = [
      ...new Set(list.map((item) => item.category).filter(Boolean)),
    ].sort();

    const uniqueYears = [
      ...new Set(list.map((item) => item.years).filter(Boolean)),
    ]
      .map(Number)
      .sort((a, b) => b - a);

    return {
      categories: uniqueCategories,
      years: uniqueYears,
    };
  }

  // ─── YouTube Links ────────────────────────────────────────────────────────

  static youtubePrefix = "/youtube-links";

  static async getAllYoutubeLinks() {
    const res = await API.get(`${this.youtubePrefix}/`);
    return res.data || [];
  }

  static async createYoutubeLink(data) {
    const res = await API.post(`${this.youtubePrefix}/`, data);
    return res.data;
  }

  static async updateYoutubeLink(id, data) {
    if (!id) throw new Error("ID is required for update");
    const res = await API.put(`${this.youtubePrefix}/${id}/`, data);
    return res.data;
  }

  static async deleteYoutubeLink(id) {
    if (!id) throw new Error("ID is required for delete");
    const res = await API.delete(`${this.youtubePrefix}/${id}/`);
    return res.data;
  }

  static async bulkUploadYoutubeLinks(file) {
    const formData = new FormData();
    formData.append("file", file);
    const res = await API.post(`${this.youtubePrefix}/bulk-upload/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
  }

  // ─── Important Links ─────────────────────────────────────────────────────

  static importantLinksPrefix = "/important-links";

  static async getAllImportantLinks() {
    const res = await API.get(`${this.importantLinksPrefix}/`);
    return res.data || [];
  }

  static async createImportantLink(data) {
    const res = await API.post(`${this.importantLinksPrefix}/`, data);
    return res.data;
  }

  static async updateImportantLink(id, data) {
    if (!id) throw new Error("ID is required for update");
    const res = await API.put(`${this.importantLinksPrefix}/${id}/`, data);
    return res.data;
  }

  static async deleteImportantLink(id) {
    if (!id) throw new Error("ID is required for delete");
    const res = await API.delete(`${this.importantLinksPrefix}/${id}/`);
    return res.data;
  }

  // ─── Gazette Notifications ────────────────────────────────────────────────

  static gazatteLinksPrefix = "/gadget-notifications";

  static async getAllGazatteLinks() {
    const res = await API.get(`${this.gazatteLinksPrefix}/`);
    return res.data || [];
  }

  static async createGazatteLink(data) {
    const res = await API.post(`${this.gazatteLinksPrefix}/`, data);
    return res.data;
  }

  static async updateGazatteLink(id, data) {
    if (!id) throw new Error("ID is required for update");
    const res = await API.put(`${this.gazatteLinksPrefix}/${id}/`, data);
    return res.data;
  }

  static async deleteGazatteLink(id) {
    if (!id) throw new Error("ID is required for delete");
    const res = await API.delete(`${this.gazatteLinksPrefix}/${id}/`);
    return res.data;
  }

  // ─── File Handling ────────────────────────────────────────────────────────

  static filePrefix = "/file";

  static async getFile(filepath) {
    if (!filepath) throw new Error("filepath is required");

    const cleanPath = filepath.trim().replace(/^\/+/, "");

    try {
      const res = await API.get("/file/get", {
        params: { filepath: cleanPath },
        responseType: "blob",
        paramsSerializer: (params) =>
          `filepath=${encodeURIComponent(params.filepath)}`,
        validateStatus: (status) =>
          (status >= 200 && status < 300) || status === 404,
      });

      if (res.status === 404) {
        throw new Error(`File not found → ${cleanPath}`);
      }

      return res.data; // Blob
    } catch (err) {
      let errorText = err.message;

      if (err.response?.data instanceof Blob) {
        try {
          errorText = await err.response.data.text();
        } catch (_) {}
      }

      console.error("Download error →", {
        requestedPath: cleanPath,
        status: err.response?.status,
        message: errorText,
      });

      throw new Error(errorText || "Download failed");
    }
  }

  static async downloadFile(filepath) {
    const blob = await this.getFile(filepath);

    const cleanPath = filepath.trim().replace(/^\/+/, "");
    const fileName = cleanPath.split("/").pop() || "document.pdf";

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  static async getActiveFilePathsOnly() {
    const res = await API.get(`${this.prefix}/all/abstract`);
    const apiItems = res.data?.data || [];

    return apiItems
      .filter((item) => String(item.status).toLowerCase() === "active")
      .map((item) => item.file_path)
      .filter(Boolean);
  }

  static async uploadFile(file) {
    if (!file) throw new Error("File is required");

    const formData = new FormData();
    formData.append("file", file);

    const res = await API.post(`${this.filePrefix}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data;
  }

  static async uploadSummaryPDF(file) {
    const formData = new FormData();
    formData.append("file", file);

    const res = await API.post("/summaries/pdf", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data;
  }
  

  static async uploadGazetteFile(file) {
    if (!file) throw new Error("File is required");

    const formData = new FormData();
    formData.append("file", file);

    const res = await API.post(`${this.filePrefix}/upload-gazette`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    return res.data;
  }

  // ─── Tracking — Login Logs ────────────────────────────────────────────────

  /**
   * Fetch paginated login history.
   * Uses sessionStorage only (no localStorage).
   */
  static async getLoginLogs(page = 1, page_size = 20, from = "", to = "") {
    // Guard: must be authenticated
    if (!sessionStorage.getItem("access")) {
      return { count: 0, results: [] };
    }

    try {
      const params = { page, page_size };
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      const res = await API.get("/tracking/logins/", { params });
      return res.data || { count: 0, results: [] };
    } catch (error) {
      console.error("Login logs fetch error:", error.response?.status);
      if (error.response?.status === 401) {
        sessionStorage.clear();
        window.location.href = "/";
      }
      return { count: 0, results: [] };
    }
  }

  // ─── Tracking — Traffic Logs ──────────────────────────────────────────────

  /**
   * Fetch paginated API traffic logs.
   * Uses sessionStorage only (no localStorage).
   */
  static async getTrafficLogs(page = 1, page_size = 20, from = "", to = "") {
    if (!sessionStorage.getItem("access")) {
      return { count: 0, results: [] };
    }

    try {
      const params = { page, page_size };
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      const res = await API.get("/tracking/traffic/", { params });
      return res.data || { count: 0, results: [] };
    } catch (error) {
      console.error("Traffic logs fetch error:", error.response?.status);
      if (error.response?.status === 401) {
        sessionStorage.clear();
        window.location.href = "/";
      }
      return { count: 0, results: [] };
    }
  }

  // ─── Tracking — Download Logs ─────────────────────────────────────────────

  /**
   * Fetch paginated download logs.
   * Uses sessionStorage only (no localStorage).
   */
  static async getDownloadLogs(page = 1, page_size = 20, from = "", to = "") {
    if (!sessionStorage.getItem("access")) {
      return { count: 0, results: [] };
    }

    try {
      const params = { page, page_size };
      if (from) params.from_date = from;
      if (to) params.to_date = to;

      const res = await API.get("/tracking/downloads/", { params });
      return res.data || { count: 0, results: [] };
    } catch (error) {
      console.error("Download logs fetch error:", error.response?.status);
      if (error.response?.status === 401) {
        sessionStorage.clear();
        window.location.href = "/";
      }
      return { count: 0, results: [] };
    }
  }

  // ─── Tracking — Active Users ──────────────────────────────────────────────

  /**
   * Fetch currently active users with their full details.
   * Returns { active_users_count, period_minutes, since, users: [...] }
   * Uses sessionStorage only (no localStorage).
   */
  static async getActiveUsers(minutes = 15) {
    if (!sessionStorage.getItem("access")) {
      return {
        active_users_count: 0,
        period_minutes: minutes,
        since: null,
        users: [],
      };
    }

    try {
      const res = await API.get("/tracking/active-users/", {
        params: { minutes },
      });
      return (
        res.data || {
          active_users_count: 0,
          period_minutes: minutes,
          since: null,
          users: [],
        }
      );
    } catch (error) {
      console.error("Active users fetch error:", error.response?.status);
      if (error.response?.status === 401) {
        sessionStorage.clear();
        window.location.href = "/";
      }
      return {
        active_users_count: 0,
        period_minutes: minutes,
        since: null,
        users: [],
      };
    }
  }

  // ─── Tracking — Track Download Event ─────────────────────────────────────

  static async trackDownload(downloadData) {
    try {
      const res = await API.post("/tracking/downloads/track/", {
        userId: downloadData.userId,
        username: downloadData.username,
        email: downloadData.email || "",
        fileId: downloadData.fileId,
        fileTitle: downloadData.fileTitle,
        downloadType: downloadData.downloadType,
        filePath: downloadData.filePath || "",
        downloadedAt: new Date().toISOString(),
      });
      return res.data;
    } catch (error) {
      console.error("Failed to track download:", error);
      // Don't throw — tracking failure should not block the actual download
      return null;
    }
  }
  static async getDownloadLogs(params = {}) {
  try {
    const res = await API.get("/tracking/downloads/", {
      params: {
        page: params.page,
        page_size: params.page_size,
        from_date: params.fromDate,
        to_date: params.toDate,
      },
    });

    return res.data;
  } catch (error) {
    console.error("Failed to fetch downloads:", error);
    return { results: [], count: 0 };
  }
}

static async getRegistrations(page = 1, limit = 10) {
  try {
    const res = await API.get("/auth/all/", {
      params: {
        page,
        page_size: limit, // (keep this for future backend support)
      },
    });

    console.log("REG DATA 👉", res.data);

    return {
      results: res.data?.users || [],   // 🔥 FIXED
      count: res.data?.total_count || 0, // 🔥 FIXED
    };
  } catch (error) {
    console.error("Failed to fetch registrations:", error);
    return { results: [], count: 0 };
  }
}
}